/**
 * One shared email layout: the approved layout builder bound to the real API.
 *
 * The layout's HTML + Liquid is the document. The EmailBuilder (layout scope:
 * Editor / Preview / HTML) edits its marked header and footer regions, frames
 * and theme styles; the HTML view edits everything. Lifecycle (rename, save,
 * publish, discard, global default, duplicate, archive / restore, delete) runs
 * through the API, and every guard (in use, default, archived, never published,
 * starter) is the server's: refusals are shown as the server words them.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Banner, Btn, Checkbox, ColorPicker, ConfirmDialog, Dropdown, Field, Icon, IconButton, Input, Modal, PanelHeader, Popover, SectionLabel, SourceEditor, Spinner,
  focusSourceEditorRange, getSourceEditor, type PreviewDevice,
} from "@v0y4ge/jrni-ui";
import { EmailBuilder, VIEWPORT, stripEditorMeta, type EmailBuilderView, type SourceField } from "@v0y4ge/jrni-email-builder";
import { countMessageLayoutBodySlots, type MessageLayoutDetail, type MessageLayoutUsageResponse } from "@v0y4ge/api-contracts";
import { builderApi, classifyFailure, failureIssues, type Failure } from "../api";
import { outcomeUnknown } from "../commands";
import { useTokens, type LayoutLibrary } from "../data";
import { layoutChips, layoutIsProvided, layoutPublishConsequence, problemsTitle, versionWhen, type Chip, type Tr } from "../model";
import type { LayoutStore } from "../useLayouts";
import { LAYOUT_BUILDER_ID, useLayoutBuilder, type LayoutNotice, type StyleField } from "../useLayoutBuilder";
import { cancelBuilderEdit, gateBuilder, insertIntoBuilder } from "../useBodyBuilder";
import { useLayoutPreview } from "../usePreview";
import { BuilderActionBar, type BarBusy } from "./BuilderActionBar";
import { Chips, CONTROL_H, EditorSkeleton, FailureBanner, FooterSkeleton, PlaceholderPicker, useBuilderFrame, usePlaceholderIconOnly } from "./common";
import { HistoryModal, type HistoryRow } from "./HistoryModal";

const SRC_ID = "msgb-layout-source";
/** The layout builder's views (it has no JSON view). */
const LAYOUT_VIEWS: EmailBuilderView[] = ["editor", "preview", "source"]; // ui-config

/**
 * ui-config: the platform's neutral style specimen placed in the body slot so an
 * author can see the layout's type, links, buttons, tables and lists. It is not
 * tenant data and is never saved; links point nowhere.
 */
const SPECIMEN = [
  "<h1>Heading one</h1>",
  "<p>Body text sits on the shared font and colours. <a href=\"#\">A text link</a> uses the link colour.</p>",
  "<h2>Heading two</h2>",
  "<h3>Heading three</h3>",
  "<a class=\"button\" href=\"#\">Button</a>",
  "<table class=\"details\" role=\"presentation\">",
  "<tr><th>Label</th><td>Value</td></tr>",
  "<tr><th>Second label</th><td>Second value</td></tr>",
  "</table>",
  "<div class=\"image-placeholder\">Image placeholder, 560 by 160</div>",
  "<ul><li>Bulleted item</li><li>Another item</li></ul>",
  "<ol><li>Numbered item</li><li>Another item</li></ol>",
  "<hr>",
  "<p class=\"note\">A small note, such as a booking reference.</p>",
].join("\n");

type Dialog = null | "publish" | "discardChanges" | "discardDraft" | "default" | "archive" | "delete" | "history" | "usage";

export interface LayoutEditorProps {
  t: Tr;
  store: LayoutStore;
  layoutKey: string;
  library: LayoutLibrary | null;
  canWrite: boolean;
  device: PreviewDevice;
  onDevice: (d: PreviewDevice) => void;
  onOpenLayout: (layoutKey: string) => void;
  onDeleted: () => void;
  onOpenTemplate: (templateId: string, messageId: string) => void;
  /** Tablet and phone: the "Show list" button that opens the rail Drawer, shown first in the header row. */
  railTrigger?: ReactNode;
}

export function LayoutEditor(p: LayoutEditorProps) {
  const { t, store, layoutKey } = p;
  useEffect(() => { store.load(layoutKey); }, [layoutKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const ds = store.detailOf(layoutKey);
  if (!ds || ds.phase === "loading") return <><div className="msgb-split"><EditorSkeleton lead={p.railTrigger} label={t("messaging.builder.loading.layoutEditor", "Loading shared layout")} /></div><FooterSkeleton /></>;
  if (ds.phase === "error") return <div className="msgb-split">{p.railTrigger ? <div className="msgb-edhead">{p.railTrigger}</div> : null}<div className="msgb-edbody"><FailureBanner t={t} failure={ds.failure} onRetry={() => store.load(layoutKey, true)} /></div></div>;
  return <ReadyLayout {...p} detail={ds.detail} refreshing={ds.refreshing} />;
}

function ReadyLayout(p: LayoutEditorProps & { detail: MessageLayoutDetail; refreshing: boolean }) {
  const { t, store, layoutKey, detail } = p;
  const work = store.workOf(layoutKey) || { name: detail.name, html: detail.html };
  const dirty = store.dirty(layoutKey);
  const busy = store.busy(layoutKey);
  const archived = detail.archived;
  // A layout the platform (or the default workspace) provides is read only here: it is changed by duplicating it.
  const provided = layoutIsProvided(detail);
  const leftoverDraft = provided && !!detail.draft;
  const canEdit = p.canWrite && !archived && !provided;
  const tokens = useTokens("unknown");
  const tokenData = tokens.data;

  const [notice, setNotice] = useState<LayoutNotice | { tone: LayoutNotice["tone"]; title: string; text: ReactNode } | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [note, setNote] = useState("");
  const [alsoDefault, setAlsoDefault] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [usage, setUsage] = useState<{ phase: "loading" | "ready" | "error"; data: MessageLayoutUsageResponse["data"] | null; failure: Failure | null }>({ phase: "loading", data: null, failure: null });
  const [usageNonce, setUsageNonce] = useState(0);
  const frame = useBuilderFrame();
  const placeholderIcon = usePlaceholderIconOnly();
  const [previewWith, setPreviewWith] = useState<string>("specimen");
  const [previewBody, setPreviewBody] = useState<{ key: string; html: string } | null>(null);
  const [savedVersions, setSavedVersions] = useState<{ version: number; html: string }[]>([]);
  const [lastSource, setLastSource] = useState(false);

  useEffect(() => { setNotice(null); setFailure(null); setConfirmation(null); setDialog(null); setRenaming(null); setPreviewWith("specimen"); setPreviewBody(null); }, [layoutKey]);

  // Which templates use this layout (the server computes it from stored definitions).
  useEffect(() => {
    let live = true;
    setUsage((u) => ({ phase: "loading", data: u.data, failure: null }));
    builderApi.getMessageLayoutUsage(layoutKey).then(
      (r) => { if (live) setUsage({ phase: "ready", data: r.data, failure: null }); },
      (e: unknown) => { if (live) setUsage((u) => ({ phase: "error", data: u.data, failure: classifyFailure(e) })); },
    );
    return () => { live = false; };
  }, [layoutKey, usageNonce, detail.revision]);

  // Published versions' sources, newest first, for restoring a removed header or footer.
  useEffect(() => {
    if (!detail.published) { setSavedVersions([]); return; }
    let live = true;
    builderApi.listMessageLayoutVersions(layoutKey, { limit: 5 }).then(async (res) => {
      const rows = res.data.filter((v) => v.state !== "draft").slice(0, 5);
      const out: { version: number; html: string }[] = [];
      for (const v of rows) {
        try { const one = await builderApi.getMessageLayoutVersion(layoutKey, v.version); out.push({ version: v.version, html: one.data.html }); } catch { /* a version that can't be read is not offered */ }
      }
      if (live) setSavedVersions(out);
    }, () => { if (live) setSavedVersions([]); });
    return () => { live = false; };
  }, [layoutKey, detail.published ? detail.published.version : null]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Preview with": the specimen, or a real template that uses this layout (its published body, placed literally).
  useEffect(() => {
    if (previewWith === "specimen") { setPreviewBody(null); return; }
    let live = true;
    builderApi.getMessageTemplate(previewWith).then((r) => {
      const c = r.data.publishedContent || r.data.draftContent;
      if (live) setPreviewBody(c && c.channel === "email" ? { key: previewWith, html: stripEditorMeta(c.email.html) } : null);
    }, () => { if (live) { setPreviewBody(null); setPreviewWith("specimen"); setNotice({ tone: "warning", title: t("messaging.builder.previewWith.failed", "That message couldn't be loaded"), text: t("messaging.builder.previewWith.failedText", "The preview shows the style specimen instead.") }); } });
    return () => { live = false; };
  }, [previewWith]); // eslint-disable-line react-hooks/exhaustive-deps

  const slotBody = previewBody ? previewBody.html : SPECIMEN;
  const fields: SourceField[] = useMemo(() => (tokenData ? tokenData.tokens.map((tk) => ({ id: tk.id, label: tk.label, link: tk.type === "link" })) : []), [tokenData]);
  const samples = useMemo(() => (tokenData ? tokenData.samples : {}), [tokenData]);
  const serverPreview = useLayoutPreview(layoutKey, dirty ? work.html : null, slotBody, true);
  const pv = serverPreview.phase === "ready" ? serverPreview.data : serverPreview.phase === "error" ? serverPreview.data : null;

  const setHtml = (html: string) => { setConfirmation(null); store.setWork(layoutKey, { ...work, html }); };
  const lb = useLayoutBuilder({
    layoutKey, name: work.name, html: work.html, setHtml, readOnly: !canEdit, slotBody, samples, fields,
    serverPreviewHtml: pv ? pv.html : null, savedVersions, notify: (n) => setNotice(n), t,
  });

  /* ---------- commands ---------- */
  const gate = () => {
    const g = gateBuilder(LAYOUT_BUILDER_ID);
    if (!g.ok) setNotice({ tone: "warning", title: t("messaging.builder.finishField", "Finish or undo the field edit first"), text: `${g.reason} ${t("messaging.builder.finishFieldText", "Your typed value is still in the field; nothing else changed.")}` });
    return g.ok;
  };
  /** What "Try again" repeats after an unknown outcome: the same intent, so the same idempotency key. */
  const again = useRef<(() => void) | null>(null);
  const run = async <T,>(fn: () => Promise<{ ok: true; value: T } | { ok: false; failure: Failure }>, ok: (v: T) => void) => {
    setFailure(null);
    again.current = () => void run(fn, ok);
    const r = await fn();
    if (r.ok) ok(r.value); else setFailure(r.failure);
    return r.ok;
  };
  const save = () => { if (gate()) void run(() => store.save(layoutKey), () => setConfirmation(t("messaging.builder.saved", "Draft saved. Nothing sends until you publish."))); };
  const publish = () => {
    const thenDefault = offerAlsoDefault && alsoDefault && p.library ? p.library.defaultLayout : null;
    void run(() => store.publish(layoutKey, note.trim() || undefined), (v) => {
      setDialog(null); setNote(""); setAlsoDefault(false);
      setConfirmation(`${t("messaging.builder.published", "Published version")} ${v.detail.published ? v.detail.published.version : ""}.`);
      setNotice({ tone: "success", title: `${t("messaging.builder.published", "Published version")} ${v.detail.published ? v.detail.published.version : ""}`,
        text: `${layoutPublishConsequence(t)} ${t("messaging.builder.publishLayout.impact", "Now sending it:")} ${v.impact.overriding + v.impact.inheriting} ${t("messaging.builder.messages", "messages")}.` });
      setUsageNonce((n) => n + 1);
      // "Also make this the global default": a second, separate command, run only after the publish succeeded.
      if (thenDefault) {
        void run(() => store.setDefault(layoutKey, thenDefault), (d) => {
          setNotice({ tone: "success", title: t("messaging.builder.default.done", "This is now the global default"),
            text: d.changed ? `${d.inheriting} ${t("messaging.builder.default.doneText", "published messages that follow the global default use it from their next email. Messages already prepared keep their layout.")}` : t("messaging.builder.default.same", "It was already the global default; nothing changed.") });
        });
      }
    }).then((ok) => { if (!ok) setDialog(null); });
  };
  const discardDraft = () => void run(() => store.discardDraft(layoutKey), () => { cancelBuilderEdit(LAYOUT_BUILDER_ID); lb.resetHistory(); setDialog(null); setConfirmation(t("messaging.builder.draftDiscardedLayout", "Draft discarded. The published version keeps being used.")); }).then((ok) => { if (!ok) setDialog(null); });
  const makeDefault = () => {
    if (!p.library) return;
    void run(() => store.setDefault(layoutKey, p.library!.defaultLayout), (v) => {
      setDialog(null);
      setNotice({ tone: "success", title: t("messaging.builder.default.done", "This is now the global default"),
        text: v.changed ? `${v.inheriting} ${t("messaging.builder.default.doneText", "published messages that follow the global default use it from their next email. Messages already prepared keep their layout.")}` : t("messaging.builder.default.same", "It was already the global default; nothing changed.") });
    }).then((ok) => { if (!ok) setDialog(null); });
  };
  const archive = () => void run(() => store.archive(layoutKey), () => { setDialog(null); setNotice({ tone: "info", title: t("messaging.builder.archived.done", "Layout archived"), text: t("messaging.builder.archived.doneText", "Messages can't choose it now. Its versions are kept; restore it any time.") }); }).then((ok) => { if (!ok) { setDialog(null); setUsageNonce((n) => n + 1); } });
  const restore = () => void run(() => store.restore(layoutKey), () => setNotice({ tone: "success", title: t("messaging.builder.restored", "Layout restored"), text: t("messaging.builder.restoredText", "Messages can choose it again.") }));
  const remove = () => void run(() => store.remove(layoutKey), () => { setDialog(null); p.onDeleted(); }).then((ok) => { if (!ok) setDialog(null); });
  const duplicate = () => {
    if (!gate()) return;
    // A provided layout is copied as published, unless it holds a draft left from before layouts became read only.
    const version = provided && !leftoverDraft ? "published" : "latest";
    void run(() => store.create(`${detail.name} ${t("messaging.builder.copySuffix", "(copy)")}`.slice(0, 160), { kind: "copy", layoutKey, version }),
      (d) => p.onOpenLayout(d.layoutKey));
  };
  const commitRename = () => {
    if (renaming == null) return;
    const v = renaming.trim();
    if (!v) return; // the field shows the error; the name stays as typed
    store.setWork(layoutKey, { ...work, name: v.slice(0, 160) });
    setRenaming(null);
  };

  /* ---------- placeholders ---------- */
  const placeholderOptions = useMemo(() => tokenData ? [
    ...tokenData.tokens.map((tk) => ({ value: tk.id, label: tk.label, supportingText: `{{ ${tk.id} }}` })),
    ...tokenData.unavailable.map((tk) => ({ value: `__off__${tk.id}`, label: tk.label, disabled: true, supportingText: (tk as { reason?: string }).reason || t("messaging.builder.tokenUnavailableHere", "Not available here") })),
  ] : [], [tokenData, t]);
  const onPlaceholder = (id: string) => {
    if (!id || id.startsWith("__off__")) return;
    const raw = `{{ ${id} }}`;
    if (lastSource) {
      const ed = getSourceEditor(SRC_ID);
      if (ed) { const s = ed.getSelection(); const html = ed.getValue(); setHtml(html.slice(0, s.from) + raw + html.slice(s.to)); setTimeout(() => focusSourceEditorRange(SRC_ID, s.from + raw.length), 0); return; }
    }
    if (!insertIntoBuilder(LAYOUT_BUILDER_ID, raw)) setNotice({ tone: "info", title: t("messaging.builder.placeholder.where", "Choose where the placeholder goes"), text: t("messaging.builder.placeholder.whereLayout", "Click into a header or footer text field, or the HTML source, first.") });
  };

  /* ---------- styles + sections (builder side panel) ---------- */
  const stylesExtra = <StyleControls t={t} styles={lb.styles} readOnly={!canEdit} />;
  const sectionsExtra = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-3)" }}>
      {!lb.sectionsSupported ? <p className="msgb-small">{lb.sectionsReason || t("messaging.builder.layoutSections.none", "This layout's header and footer can't be managed here; edit them in HTML.")}</p> : null}
      {lb.sectionRows.map((r) => (
        <div key={r.key} style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }}>
          <SectionLabel>{r.label}</SectionLabel>
          <p className="msgb-small">{r.summary}</p>
          <div className="msgb-actions">
            {r.action && r.onAction ? <Btn size="sm" variant="secondary" disabled={!canEdit} onClick={r.onAction}>{r.action}</Btn> : null}
            {r.restore && r.onRestore ? <Btn size="sm" variant="secondary" disabled={!canEdit} onClick={r.onRestore}>{r.restore}</Btn> : null}
            <Btn size="sm" variant="secondary" disabled={!canEdit} onClick={r.onAdd}>{r.add}</Btn>
          </div>
          {r.added.map((a) => (
            <div key={a.key} className="msgb-actions"><span className="msgb-small">{a.label}</span><Btn size="sm" variant="link" disabled={!canEdit} onClick={a.onRemove}>{t("messaging.builder.remove", "Remove")}</Btn></div>
          ))}
        </div>
      ))}
    </div>
  );
  const usedBy = usage.data ? usage.data.published : [];
  const previewOptions = [{ value: "specimen", label: t("messaging.builder.previewWith.specimen", "Style specimen") },
    ...usedBy.map((u) => ({ value: u.templateId, label: `${u.name}${u.locale ? ` (${u.locale})` : ""}` }))];
  const previewExtra = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }}>
      <Dropdown portal label={t("messaging.builder.previewWith", "Preview with")} options={previewOptions} value={previewWith}
        onChange={(v) => setPreviewWith(String(v))} />
      <p className="msgb-small">{previewWith === "specimen"
        ? t("messaging.builder.previewWith.specimenText", "A neutral specimen shows the layout's type, links, buttons, tables and lists.")
        : t("messaging.builder.previewWith.templateText", "The message's published body is placed in the slot as written; its placeholders are not filled in here.")}</p>
    </div>
  );

  /* ---------- header ---------- */
  const isStarter = !!detail.provenance.starterKey;
  const usageText = usage.data
    ? `${t("messaging.builder.usage.used", "Used by")} ${usage.data.published.length} ${t("messaging.builder.usage.published", "published messages")}${usage.data.drafts.length ? `, ${usage.data.drafts.length} ${t("messaging.builder.usage.drafts", "drafts")}` : ""}${usage.data.isDefault ? ` ${t("messaging.builder.usage.plusDefault", "plus every message that follows the global default")}` : ""}.`
    : usage.phase === "loading" ? t("messaging.builder.usage.loading", "Checking which messages use it…") : "";
  const cannotDefault = !detail.published ? t("messaging.builder.default.unpublished", "Publish it first.") : archived ? t("messaging.builder.default.archived", "Restore it first.") : detail.isDefault ? t("messaging.builder.default.already", "It already is.") : null;
  const canDelete = !provided && !detail.published && !isStarter && !detail.isDefault;
  const copiedFrom = detail.provenance.copiedFrom ?? null;
  const copiedFromName = copiedFrom ? (p.library?.layouts.find((l) => l.layoutKey === copiedFrom.layoutKey)?.name ?? copiedFrom.layoutKey) : null;
  const copiedFromText = copiedFrom ? (copiedFrom.version != null
    ? `${t("messaging.builder.copiedFrom", "Copied from")} ${copiedFromName}, ${t("messaging.builder.version", "version")} ${copiedFrom.version}`
    : `${t("messaging.builder.copiedFrom", "Copied from")} ${copiedFromName} ${t("messaging.builder.copiedFrom.draft", "(unpublished changes)")}`) : null;
  // Publishing a copy of the current global default can make it the default in the same step.
  const offerAlsoDefault = !!copiedFrom && !!p.library && p.library.defaultLayout.layoutKey === copiedFrom.layoutKey && !detail.isDefault && !provided;
  const providedTitle = detail.managedBy === "workspace_default"
    ? t("messaging.builder.provided.workspaceTitle", "Provided by your organisation's default workspace")
    : t("messaging.builder.provided.platformTitle", "Provided by the platform");
  const duplicateLabel = t("messaging.builder.duplicateToEdit", "Duplicate to edit");

  // Only a wrong {{ body }} slot count blocks (the contract refuses it on save, preview and publish). A layout
  // the visual editor can't read is still fully editable in HTML: that is a paused editor, not a problem.
  const slots = countMessageLayoutBodySlots(work.html);
  const slotProblem = slots === 1 ? null : slots === 0
    ? t("messaging.builder.layout.noSlot", "A layout needs exactly one {{ body }} slot; this one has none.")
    : `${t("messaging.builder.layout.slotsPrefix", "A layout needs exactly one {{ body }} slot; this one has")} ${slots}.`;
  const issues = [
    ...(slotProblem ? [slotProblem] : []),
    ...(failure && failure.kind === "invalid" ? failureIssues(failure).map((i) => i.message) : []),
  ];
  const renderWarnings = pv ? pv.warnings.map((w) => w.message) : [];
  const barBusy: BarBusy = busy === "discard" ? "discardDraft" : busy === "save" || busy === "publish" ? busy : null;
  // The Platform / Workspace default chip comes from layoutChips; "Read only" is kept for no permission only.
  const headChips: Chip[] = [...layoutChips(detail, t),
    ...(!p.canWrite && !archived ? [{ tone: "neutral" as const, label: t("messaging.builder.readOnly", "Read only") }] : [])];

  return (
    <>
      <div className="msgb-split">
        <div className="msgb-edhead">
          {p.railTrigger}
          <PanelHeader as="h2" size="md" divider={false}
            title={renaming == null ? (
              <span className="msgb-titlerow">
                <span className="msgb-ellipsis">{work.name}</span>
                {!provided ? <IconButton size={CONTROL_H} label={`${t("messaging.builder.rename", "Rename layout")}: ${work.name}`} disabled={!canEdit} onClick={() => setRenaming(work.name)}><Icon.edit size={14} /></IconButton> : null}
                <span className="msgb-chipset msgb-hide-tablet"><Chips chips={headChips} /></span>
              </span>
            ) : (
              <span className="msgb-renamebox">
                <Input id="msgb-layout-rename" size="sm" full autoFocus aria-label={t("messaging.builder.layoutName", "Layout name")}
                  aria-invalid={!renaming.trim()} aria-describedby="msgb-layout-rename-err" value={renaming} maxLength={160}
                  onChange={(e) => setRenaming(e.target.value)} onBlur={commitRename}
                  onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(null); }} />
                {!renaming.trim() ? <span className="msgb-fielderr" id="msgb-layout-rename-err" role="alert">{t("messaging.builder.layoutName.required", "Give the layout a name.")}</span> : null}
              </span>
            )}
            description={
              <span style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2)", minWidth: 0 }}>
                <span className="msgb-ellipsis">
                  {/* tablet and phone: the status chips read here, so the title keeps one line */}
                  <span className="msgb-show-tablet">{`${headChips.map((c) => c.label).join(", ")} · `}</span>
                  {[t("messaging.builder.sharedLayout", "Shared layout"), copiedFromText, usageText].filter(Boolean).join(" · ")}
                </span>
                {usage.data && (usage.data.published.length || usage.data.drafts.length || usage.data.history.length) ? (
                  <span style={{ flex: "none" }}><Btn variant="link" size="sm" onClick={() => setDialog("usage")}>{t("messaging.builder.usage.view", "See which")}</Btn></span>
                ) : null}
              </span>
            }
            actions={p.canWrite ? (
              <div className="msgb-headactions">
                {archived ? (
                  <Btn variant="secondary" size="md" onClick={restore} disabled={busy !== null} aria-busy={busy === "restore"} icon={busy === "restore" ? <Spinner size="sm" label={t("messaging.builder.working", "Working")} /> : undefined}>
                    {t("messaging.builder.restore", "Restore from archive")}
                  </Btn>
                ) : (
                  <>
                    <Btn variant="secondary" size="md" disabled={!!cannotDefault || busy !== null} title={cannotDefault || undefined} onClick={() => setDialog("default")}>{t("messaging.builder.makeDefault", "Make global default")}</Btn>
                    {!provided ? <Btn variant="secondary" size="md" disabled={busy !== null} onClick={duplicate}>{t("messaging.builder.duplicate", "Duplicate")}</Btn> : null}
                    {canDelete ? (
                      <Btn variant="secondary" size="md" disabled={busy !== null} onClick={() => setDialog("delete")}>{t("messaging.builder.delete", "Delete")}</Btn>
                    ) : detail.managedBy === "workspace_default" ? null : (
                      <Btn variant="secondary" size="md" disabled={busy !== null || detail.isDefault} title={detail.isDefault ? t("messaging.builder.archive.default", "Make another layout the global default first.") : undefined} onClick={() => setDialog("archive")}>{t("messaging.builder.archive", "Archive")}</Btn>
                    )}
                  </>
                )}
              </div>
            ) : undefined} />
        </div>

        <div className="msgb-edbody">
          {usage.phase === "error" && usage.failure ? <FailureBanner t={t} failure={usage.failure} onRetry={() => setUsageNonce((n) => n + 1)} /> : null}
          {provided && !archived ? (
            <Banner tone="info" size="compact" title={providedTitle} testid="msgb-layout-provided"
              action={p.canWrite ? <Btn size="sm" variant="primary" disabled={busy !== null} onClick={duplicate} testid="msgb-layout-provided-duplicate">{duplicateLabel}</Btn> : undefined}>
              {leftoverDraft && p.canWrite
                ? t("messaging.builder.provided.leftover", "This layout has unpublished changes from before layouts became read-only. Duplicate it to keep them in your own copy, or discard them.")
                : t("messaging.builder.provided.text", "Read only. Duplicate it to make your own version.")}
            </Banner>
          ) : null}
          {archived ? <Banner tone="neutral" size="compact" title={t("messaging.builder.archived.title", "Archived")}>{t("messaging.builder.archived.text", "Messages can't choose an archived layout. Restore it to edit or use it.")}</Banner> : null}
          {notice ? <Banner tone={notice.tone} size="compact" title={notice.title} onDismiss={() => setNotice(null)}>{notice.text}</Banner> : null}
          {store.warnings(layoutKey).length ? <Banner tone="warning" size="compact" title={t("messaging.builder.notApplied", "Part of the change was not applied")}><ul className="msgb-problems">{store.warnings(layoutKey).map((w, i) => <li key={i}>{w.message}</li>)}</ul></Banner> : null}
          {issues.length ? <Banner tone="danger" size="compact" title={problemsTitle(issues.length, t)}><ul className="msgb-problems">{issues.map((m, i) => <li key={i}>{m}</li>)}</ul></Banner> : null}
          {lb.stale && !slotProblem ? (
            <Banner tone="warning" size="compact" title={t("messaging.builder.paused", "Visual editing is paused")} testid="msgb-layout-paused">
              {t("messaging.builder.layout.pausedText", "This layout can't be edited visually")}: {lb.stale}. {t("messaging.builder.layout.pausedHtml", "Everything stays editable in the HTML view, and publishing is not blocked by this.")}
            </Banner>
          ) : null}
          {lb.regionIssues ? <Banner tone="warning" size="compact" title={t("messaging.builder.layout.partial", "Part of this layout is HTML only")}>{lb.regionIssues}</Banner> : null}
          {renderWarnings.length ? <Banner tone="warning" size="compact" title={t("messaging.builder.check", "Check before publishing")}><ul className="msgb-problems">{renderWarnings.map((m, i) => <li key={i}>{m}</li>)}</ul></Banner> : null}
          {serverPreview.phase === "error" ? <FailureBanner t={t} failure={serverPreview.failure} onRetry={serverPreview.retry}>{" "}{t("messaging.builder.previewFailed", "The editor keeps working; the server preview will update once this succeeds.")}</FailureBanner> : null}
        </div>

        <div className="msgb-builderhost">
          <EmailBuilder
            id={LAYOUT_BUILDER_ID}
            scope="layout"
            frame={frame}
            density="compact"
            fluidDesktop
            views={LAYOUT_VIEWS}
            readOnly={!canEdit}
            documentKey={lb.documentKey}
            title={lb.title}
            srcdoc={lb.srcdoc}
            previewSrcdoc={lb.previewSrcdoc}
            nodes={lb.nodes as never}
            doc={lb.doc}
            rootIds={lb.rootIds}
            branches={lb.branches}
            selectedId={lb.selectedId}
            onSelect={lb.onSelect}
            onOp={lb.onOp}
            canUndo={lb.canUndo} canRedo={lb.canRedo} onUndo={lb.onUndo} onRedo={lb.onRedo}
            device={p.device} deviceWidths={VIEWPORT.widths} onDevice={p.onDevice}
            bodyLocked
            layoutName={work.name}
            onEditLayout={() => undefined}
            regions={lb.regions}
            onRegion={lb.onRegion}
            canAddBlock={canEdit && lb.canAddBlock}
            addBlockHint={lb.addBlockHint || undefined}
            linkOptions={fields.filter((f) => f.link).map((f) => ({ value: String(f.id), label: String(f.label) }))}
            onEditSource={(id) => {
              if (!gate()) return;
              const r = lb.sourceRange(id);
              setSourceOpen(true);
              setTimeout(() => focusSourceEditorRange(SRC_ID, r ? r.start : 0, r ? r.end : 0), 0);
            }}
            onFocusText={() => setLastSource(false)}
            onImportValidate={() => ({ ok: false, reason: t("messaging.builder.layoutImport", "Layouts are edited in place; JSON import is for message bodies.") })}
            onImportApply={() => ({ ok: false, reason: t("messaging.builder.layoutImport", "Layouts are edited in place; JSON import is for message bodies.") })}
            stylesExtra={stylesExtra}
            sectionsExtra={sectionsExtra}
            previewExtra={previewExtra}
            toolbarSlot={
              <PlaceholderPicker t={t} options={placeholderOptions} onPick={onPlaceholder} disabled={!canEdit} iconOnly={placeholderIcon} />
            }
            sourceOpen={sourceOpen}
            onSourceOpen={(o) => { if (!o || gate()) setSourceOpen(o); }}
            sourcePanel={
              <SourceEditor id={SRC_ID} label={t("messaging.builder.layoutHtml", "Layout HTML")} language="liquid-html"
                docKey={layoutKey} fill wrap value={work.html} readOnly={!canEdit}
                onChange={setHtml} onFocus={() => setLastSource(true)}
                formatGuards={[/\{\{\s*body\s*\}\}/g]}
                completions={fields.map((f) => ({ id: String(f.id), detail: String(f.label) }))} />
            }
          />
        </div>
      </div>

      <BuilderActionBar t={t} testid="msgb-layout-bar" dirty={dirty} busy={barBusy} readOnly={!p.canWrite || archived} canEditContent={canEdit}
        hasServerDraft={!!detail.draft && !!detail.published}
        publishableDraft={!!detail.draft}
        publishBlocked={slotProblem ? problemsTitle(1, t) : null}
        blocked={slotProblem}
        note={!p.canWrite ? t("messaging.builder.readOnly.permission", "You can view this message but not change it.")
          : provided ? `${providedTitle}. ${t("messaging.builder.provided.text", "Read only. Duplicate it to make your own version.")}`
          : t("messaging.builder.layoutSaveNote", "Saving a draft never changes what sends. Publishing applies from the next message prepared, for every message that uses this layout.")}
        confirmation={confirmation}
        failure={failure ? (failure.kind === "readOnly"
          ? <FailureBanner t={t} failure={failure} onDismiss={() => setFailure(null)} onRetry={p.canWrite ? duplicate : undefined} retryLabel={duplicateLabel} />
          : <FailureBanner t={t} failure={failure} onDismiss={() => setFailure(null)} onRetry={outcomeUnknown(failure) && again.current ? () => again.current && again.current() : undefined} />) : null}
        endAction={provided && p.canWrite && !archived ? (
          <Btn variant="primary" size="md" disabled={busy !== null} onClick={duplicate} icon={<Icon.copy size={14} />} testid="msgb-layout-bar-duplicate">{duplicateLabel}</Btn>
        ) : null}
        onSave={save}
        onDiscardChanges={() => setDialog("discardChanges")}
        onDiscardDraft={() => { if (gate()) setDialog("discardDraft"); }}
        onPublish={() => { if (gate()) setDialog("publish"); }}
        onHistory={() => { if (gate()) setDialog("history"); }} />

      <ConfirmDialog open={dialog === "publish"} tone="warning" busy={busy === "publish" || busy === "save"} title={`${t("messaging.builder.publishTitle", "Publish")} ${work.name}?`}
        body={layoutPublishConsequence(t)}
        consequence={usage.data ? `${usage.data.published.length} ${t("messaging.builder.usage.published", "published messages")}${usage.data.isDefault ? ` ${t("messaging.builder.usage.plusDefault", "plus every message that follows the global default")}` : ""} ${t("messaging.builder.publishLayout.willUse", "will use it.")}` : undefined}
        confirmLabel={t("messaging.builder.publish", "Publish")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={publish} testid="msgb-layout-publish-confirm">
        <Field label={t("messaging.builder.versionNote", "Version note (optional)")} htmlFor="msgb-layout-note" hint={t("messaging.builder.versionNoteHint", "Shown in Version history.")}>
          <Input id="msgb-layout-note" full value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {offerAlsoDefault ? (
          <Checkbox checked={alsoDefault} onChange={setAlsoDefault} testid="msgb-layout-publish-also-default"
            label={t("messaging.builder.publish.alsoDefault", "Also make this the global default")} />
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog open={dialog === "default"} tone="warning" busy={busy === "default"} title={`${t("messaging.builder.makeDefault", "Make global default")}: ${work.name}?`}
        body={t("messaging.builder.default.body", "Every message that follows the global default uses this layout from its next email. Messages that override the layout are not changed, and messages already prepared keep their layout.")}
        consequence={provided ? t("messaging.builder.default.provided", "Messages that follow the global default will use it exactly as provided. To change how it looks, duplicate it first, publish the copy, then make the copy the global default.") : undefined}
        confirmLabel={t("messaging.builder.makeDefault", "Make global default")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={makeDefault} testid="msgb-default-confirm" />
      <ConfirmDialog open={dialog === "archive"} tone="warning" busy={busy === "archive"} title={`${t("messaging.builder.archive", "Archive")} ${work.name}?`}
        body={t("messaging.builder.archive.body", "Archived layouts can't be chosen by messages. Its versions are kept and you can restore it later.")}
        consequence={usage.data && (usage.data.published.length || usage.data.drafts.length) ? t("messaging.builder.archive.inUse", "Messages still use it, so the server will refuse until they choose another layout.") : undefined}
        confirmLabel={t("messaging.builder.archive", "Archive")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={archive} testid="msgb-archive-confirm" />
      <ConfirmDialog open={dialog === "delete"} tone="danger" busy={busy === "delete"} title={`${t("messaging.builder.delete", "Delete")} ${work.name}?`}
        body={t("messaging.builder.delete.body", "This layout was never published. Deleting removes it and its drafts for good.")}
        confirmLabel={t("messaging.builder.delete", "Delete")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={remove} testid="msgb-delete-confirm" />
      <ConfirmDialog open={dialog === "discardChanges"} tone="neutral" title={t("messaging.builder.discardTitle", "Discard unsaved changes?")}
        body={t("messaging.builder.discardChangesBody", "Your edits since the last save are removed. The saved draft and the published version are not changed.")}
        confirmLabel={t("messaging.builder.discard", "Discard changes")} cancelLabel={t("messaging.builder.keepEditing", "Keep editing")}
        onCancel={() => setDialog(null)} onConfirm={() => { cancelBuilderEdit(LAYOUT_BUILDER_ID); store.discardLocal(layoutKey); lb.resetHistory(); setDialog(null); }} />
      <ConfirmDialog open={dialog === "discardDraft"} tone="warning" busy={busy === "discard"} title={t("messaging.builder.discardDraftTitle", "Discard the saved draft?")}
        body={t("messaging.builder.discardDraftLayoutBody", "The draft is removed. The published version keeps being used by every message.")}
        confirmLabel={t("messaging.builder.discardDraft", "Discard draft")} cancelLabel={t("messaging.builder.cancel", "Cancel")}
        onCancel={() => setDialog(null)} onConfirm={discardDraft} />
      <UsageModal t={t} open={dialog === "usage"} onClose={() => setDialog(null)} name={work.name} usage={usage.data} onOpenTemplate={(id, mid) => { setDialog(null); p.onOpenTemplate(id, mid); }} />
      <HistoryModal t={t} open={dialog === "history"} onClose={() => setDialog(null)} subject={work.name}
        provenance={<LayoutProvenance t={t} detail={detail} />}
        load={async () => {
          const res = await builderApi.listMessageLayoutVersions(layoutKey, { limit: 100 });
          return res.data.map((v): HistoryRow => ({ version: v.version, name: v.name, state: v.state, createdAt: v.createdAt, createdBy: v.createdBy, note: v.note, publishedAt: v.publishedAt }));
        }}
        onRestore={canEdit ? async (version) => {
          if (!gate()) return t("messaging.builder.finishField", "Finish or undo the field edit first");
          try {
            const res = await builderApi.getMessageLayoutVersion(layoutKey, version);
            store.setWork(layoutKey, { name: work.name, html: res.data.html });
            setNotice({ tone: "info", title: `${t("messaging.builder.history.loaded", "Version")} ${version} ${t("messaging.builder.history.loadedSuffix", "is in the editor")}`, text: t("messaging.builder.history.loadedText", "It is not saved yet. Save a draft to keep it, then publish to send it.") });
            return null;
          } catch {
            return t("messaging.builder.history.loadFailed", "That version couldn't be loaded. Nothing was changed.");
          }
        } : null} />
    </>
  );
}

function LayoutProvenance({ t, detail }: { t: Tr; detail: MessageLayoutDetail }) {
  const pr = detail.provenance;
  const lines = [
    pr.starterKey ? t("messaging.builder.prov.starter", "One of the platform's starter layouts.") : pr.origin === "seed" ? t("messaging.builder.prov.seed", "Started from the platform's default content.") : t("messaging.builder.prov.authoredLayout", "Created by your team."),
    pr.ownershipTransferredAt ? `${t("messaging.builder.prov.transferred", "Taken over from the platform default")} ${versionWhen(pr.ownershipTransferredAt)}.` : null,
    `${t("messaging.builder.prov.updated", "Last changed")} ${versionWhen(pr.updatedAt)}${pr.updatedBy ? ` ${t("messaging.builder.prov.by", "by")} ${pr.updatedBy}` : ""}.`,
  ].filter(Boolean) as string[];
  return <div className="msgb-meta">{lines.map((l) => <span key={l}>{l}</span>)}</div>;
}

function UsageModal({ t, open, onClose, name, usage, onOpenTemplate }: {
  t: Tr; open: boolean; onClose: () => void; name: string; usage: MessageLayoutUsageResponse["data"] | null; onOpenTemplate: (templateId: string, messageId: string) => void;
}) {
  const group = (title: string, rows: MessageLayoutUsageResponse["data"]["published"], text: string) => rows.length ? (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }}>
      <SectionLabel>{title} ({rows.length})</SectionLabel>
      <p className="msgb-small">{text}</p>
      <ul className="msgb-problems">
        {rows.map((r, i) => (
          // The usage read can list one template twice (one row per locale or binding source), so the index keeps keys unique.
          <li key={`${r.templateId}|${r.binding}|${r.locale ?? ""}|${i}`} style={{ overflowWrap: "anywhere", minWidth: 0 }}>
            <Btn variant="link" size="sm" onClick={() => onOpenTemplate(r.templateId, r.messageId)}
              style={{ whiteSpace: "normal", height: "auto", minHeight: 0, textAlign: "left", overflowWrap: "anywhere", maxWidth: "100%" }}>{r.name}{r.locale ? ` (${r.locale})` : ""}</Btn>
            {" "}<span className="msgb-small">{r.binding === "default" ? t("messaging.builder.usage.viaDefault", "follows the global default") : t("messaging.builder.usage.override", "chose this layout")}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;
  return (
    <Modal open={open} onClose={onClose} title={t("messaging.builder.usage.title", "Messages that use this layout")} description={name} maxWidth={560}>
      <Modal.Body>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-3)" }}>
          {usage ? (
            <>
              {usage.isDefault ? <Banner tone="info" size="compact" title={t("messaging.builder.usage.isDefault", "This is the global default")}>{t("messaging.builder.usage.isDefaultText", "Every message that follows the global default uses it too.")}</Banner> : null}
              {group(t("messaging.builder.usage.sending", "Sending with it"), usage.published, t("messaging.builder.usage.sendingText", "These block archiving."))}
              {group(t("messaging.builder.usage.inDrafts", "Chosen in a draft"), usage.drafts, t("messaging.builder.usage.inDraftsText", "These block archiving too."))}
              {group(t("messaging.builder.usage.history", "Only in earlier versions"), usage.history, t("messaging.builder.usage.historyText", "These don't block archiving, but the layout can't be deleted."))}
            </>
          ) : <p className="msgb-small">{t("messaging.builder.usage.loading", "Checking which messages use it…")}</p>}
        </div>
      </Modal.Body>
    </Modal>
  );
}

/** Theme variables or colours of the layout's CSS, written back exactly (the builder's Styles panel). */
function StyleControls({ t, styles, readOnly }: { t: Tr; styles: { kind: "noCss" } | { kind: "vars"; fields: StyleField[] } | { kind: "colors"; fields: StyleField[] } | { kind: "none" }; readOnly: boolean }) {
  if (styles.kind === "noCss") return <p className="msgb-small">{t("messaging.builder.styles.noCss", "This layout has no shared style block, so its styles are edited in HTML.")}</p>;
  if (styles.kind === "none") return <p className="msgb-small">{t("messaging.builder.styles.none", "No colours or theme variables to edit here. Edit the CSS in HTML.")}</p>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-3)" }}>
      <p className="msgb-small">{styles.kind === "vars" ? t("messaging.builder.styles.vars", "Theme values used across this layout.") : t("messaging.builder.styles.colors", "Colours used in this layout's CSS.")}</p>
      {styles.fields.map((f) => <StyleFieldRow key={f.key} field={f} readOnly={readOnly} />)}
    </div>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;

/** Compact row (as approved): a swatch button opening the DS ColorPicker in a Popover, beside the exact value. */
function StyleFieldRow({ field, readOnly }: { field: StyleField; readOnly: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const value = draft ?? field.value;
  const problem = draft != null ? field.problem(draft) : null;
  const commit = (v: string) => { const pr = field.problem(v); if (pr) { setDraft(v); return; } setDraft(null); if (v !== field.value) field.write(v); };
  const id = `msgb-style-${field.key.replace(/[^\w-]/g, "")}`;
  const hex = HEX.test(value.trim()) ? value.trim() : null;
  return (
    <Field label={field.label} hint={problem ? undefined : field.hint} error={problem || undefined} htmlFor={id}>
      <div style={{ display: "flex", gap: "var(--jrni-space-2)", alignItems: "center", minWidth: 0 }}>
        {field.picker ? (
          <div style={{ position: "relative", flex: "none" }}>
            <Btn size="sm" variant="secondary" disabled={readOnly || !hex} aria-label={field.label} aria-haspopup="dialog" aria-expanded={open}
              onClick={() => setOpen(!open)} style={{ padding: "0 var(--jrni-space-2)", minWidth: 0 }}>
              <span aria-hidden="true" style={{ display: "inline-block", width: 18, height: 18, borderRadius: "var(--jrni-radius-sm)", border: "1px solid var(--jrni-color-surface-border)", background: hex || "transparent" }} />
            </Btn>
            <Popover open={open} onClose={() => setOpen(false)} align="left" width={284} portal>
              <div style={{ padding: "var(--jrni-space-3)" }}>
                <ColorPicker value={hex || "#000000"} aria-label={field.label} style={{ width: "100%", minWidth: 0 }} onChange={(x: string) => commit(String(x).toUpperCase())} />
              </div>
            </Popover>
          </div>
        ) : null}
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Input id={id} size="sm" full value={value} readOnly={readOnly} aria-invalid={problem ? true : undefined}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { if (draft != null) commit(draft); }} onKeyDown={(e) => { if (e.key === "Enter" && draft != null) commit(draft); }} />
        </div>
      </div>
    </Field>
  );
}
